import React from 'react';
import { X, BookOpenCheck, Award, TrendingUp } from 'lucide-react';
import { UserListItem, DetailedUserStats } from '../types';

interface UserStatsModalProps {
  user: UserListItem;
  stats: DetailedUserStats;
  onClose: () => void;
}

const UserStatsModal: React.FC<UserStatsModalProps> = ({ user, stats, onClose }) => {
  // Debug logging to see what data we're receiving
  console.log('UserStatsModal - Full stats object:', stats);
  console.log('UserStatsModal - Course history:', stats.courseHistory);
  console.log('UserStatsModal - Courses started:', stats.coursesStarted);
  console.log('UserStatsModal - Courses completed:', stats.coursesCompleted);

  const getStatusBadge = (progress: number, completedAt: Date | null) => {
    if (completedAt) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <Award className="w-3 h-3 mr-1" />
          Completed
        </span>
      );
    } else if (progress > 0) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <TrendingUp className="w-3 h-3 mr-1" />
          {progress}% Complete
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          Not Started
        </span>
      );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">User Statistics: {user.name}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Stats Overview Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gray-700 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-400">Exams Taken</h3>
            <p className="text-2xl font-bold">{stats.examsTaken}</p>
          </div>
          <div className="bg-gray-700 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-400">Average Score</h3>
            <p className="text-2xl font-bold text-blue-400">{stats.averageScore.toFixed(1)}%</p>
          </div>
          <div className="bg-gray-700 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-400">Courses Started</h3>
            <p className="text-2xl font-bold text-purple-400">{stats.coursesStarted}</p>
          </div>
          <div className="bg-gray-700 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-400">Courses Completed</h3>
            <p className="text-2xl font-bold text-green-400">{stats.coursesCompleted}</p>
          </div>
        </div>

        {/* Content Grid - Two Columns */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Exam History */}
          <div>
            <h3 className="text-xl font-semibold mb-4">Exam History</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Exam Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Score</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {stats.examHistory.map((exam, index) => (
                    <tr key={index}>
                      <td className="px-4 py-3 whitespace-nowrap">{exam.examName}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          exam.score >= 70 ? 'bg-green-500/20 text-green-300' :
                          exam.score >= 60 ? 'bg-yellow-500/20 text-yellow-300' :
                          'bg-red-500/20 text-red-300'
                        }`}>
                          {exam.score.toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-300">
                        {new Date(exam.completedAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                  {stats.examHistory.length === 0 && (
                    <tr>
                      <td colSpan={3} className="px-4 py-3 text-center text-gray-400">
                        No exam history available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Course History */}
          <div>
            <h3 className="text-xl font-semibold mb-4 flex items-center">
              <BookOpenCheck className="mr-2" />
              Course History
            </h3>
            <div className="space-y-3">
              {stats.courseHistory && stats.courseHistory.length > 0 ? (
                stats.courseHistory.map((course, index) => (
                  <div key={index} className="bg-gray-700 p-4 rounded-lg">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-semibold text-white">{course.courseName}</h4>
                      {getStatusBadge(course.progress, course.completedAt)}
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="mb-3">
                      <div className="flex justify-between text-sm text-gray-400 mb-1">
                        <span>Progress</span>
                        <span>{course.progress}%</span>
                      </div>
                      <div className="w-full bg-gray-600 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full transition-all duration-300 ${
                            course.completedAt ? 'bg-green-500' : 'bg-blue-500'
                          }`}
                          style={{ width: `${course.progress}%` }}
                        ></div>
                      </div>
                    </div>

                    {/* Dates */}
                    <div className="text-xs text-gray-400 space-y-1">
                      {course.completedAt && (
                        <div>
                          <span className="font-medium">Completed:</span> {new Date(course.completedAt).toLocaleDateString()}
                        </div>
                      )}
                      <div>
                        <span className="font-medium">Last Accessed:</span> {new Date(course.lastAccessed).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="bg-gray-700 p-4 rounded-lg text-center text-gray-400">
                  No course history available
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Additional Stats */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="bg-gray-700 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-400">Highest Exam Score</h3>
            <p className="text-2xl font-bold text-green-400">{stats.highestScore.toFixed(1)}%</p>
          </div>
          <div className="bg-gray-700 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-400">Lowest Exam Score</h3>
            <p className="text-2xl font-bold text-red-400">{stats.lowestScore.toFixed(1)}%</p>
          </div>
          <div className="bg-gray-700 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-400">Avg Course Progress</h3>
            <p className="text-2xl font-bold text-blue-400">{stats.averageCourseProgress.toFixed(1)}%</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserStatsModal;